<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import LeasesAPI from 'dashboard/api/leases';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LeaseOperationPanel from 'dashboard/routes/dashboard/leases/components/LeaseOperationPanel.vue';
import { useAlert } from 'dashboard/composables';
import LaisPriceBreakdown from 'dashboard/components-next/LaisPriceBreakdown.vue';
import {
  PRICE_ROW_KINDS,
  RENTAL_GUARANTEE_TYPES,
} from 'dashboard/constants/lais/imovel';
import { buildPriceBreakdownRows } from 'dashboard/helper/lais/imovelPresenter';

const tabs = [
  { id: 'operacao', label: 'Operação ativa' },
  { id: 'resumo', label: 'Resumo' },
  { id: 'valores', label: 'Valores' },
  { id: 'contrato', label: 'Contrato' },
  { id: 'apresentacao', label: 'Apresentação' },
  { id: 'timeline', label: 'Linha do tempo' },
];

const route = useRoute();
const router = useRouter();
const activeTab = ref('resumo');
const loading = ref(false);
const loadError = ref('');
const rentFlows = ref([]);
const selectedFlowId = ref('');
const activeLeases = ref([]);
const selectedLeaseId = ref('');
const leasesLoading = ref(false);
const leasesError = ref('');

const validTabIds = new Set(tabs.map(tab => tab.id));

const normalizeTab = value =>
  validTabIds.has(String(value || '')) ? String(value) : 'resumo';

const routeOfferId = computed(
  () => route.params.offerId || route.query.offer_id || route.query.contract_id
);

const routeSection = computed(() => route.params.section || route.query.tab);

const selectFlowFromRoute = () => {
  const requestedId = String(routeOfferId.value || '');
  const matchedFlow = requestedId
    ? rentFlows.value.find(flow => String(flow.id) === requestedId)
    : null;

  selectedFlowId.value = matchedFlow?.id || rentFlows.value[0]?.id || '';
};

const fetchRentFlows = async () => {
  loading.value = true;
  loadError.value = '';
  try {
    const { data } = await PropertyOffersAPI.get({
      business_context: 'rental',
      status_bucket: 'finished',
    });
    rentFlows.value = data.data || [];
    selectFlowFromRoute();
  } catch (error) {
    rentFlows.value = [];
    selectedFlowId.value = '';
    loadError.value =
      error.response?.data?.errors?.[0] ||
      error.response?.data?.message ||
      'Não foi possível carregar os dados do aluguel.';
    useAlert(loadError.value);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchRentFlows);

const fetchActiveLeases = async () => {
  leasesLoading.value = true;
  leasesError.value = '';
  try {
    const { data } = await LeasesAPI.list({ status: 'active', limit: 25 });
    activeLeases.value = data.data || [];
    selectedLeaseId.value =
      activeLeases.value.find(
        lease => String(lease.id) === String(route.query.lease_id || '')
      )?.id ||
      activeLeases.value[0]?.id ||
      '';
    if (selectedLeaseId.value && !routeSection.value && !routeOfferId.value) {
      activeTab.value = 'operacao';
    }
  } catch (error) {
    activeLeases.value = [];
    selectedLeaseId.value = '';
    leasesError.value =
      error.response?.data?.errors?.[0] ||
      'Não foi possível carregar locações ativas.';
  } finally {
    leasesLoading.value = false;
  }
};

onMounted(fetchActiveLeases);

watch(routeOfferId, () => selectFlowFromRoute());

watch(
  routeSection,
  value => {
    activeTab.value = normalizeTab(value);
  },
  { immediate: true }
);

const activeContract = computed(
  () =>
    rentFlows.value.find(
      flow => String(flow.id) === String(selectedFlowId.value)
    ) || rentFlows.value[0]
);

const currentAccountId = computed(() => route.params.accountId);

const pushCurrentRentRoute = ({ offerId, section }) => {
  const normalizedSection = normalizeTab(section);
  const normalizedOfferId = offerId || activeContract.value?.id;

  if (!normalizedOfferId) {
    activeTab.value = normalizedSection;
    return;
  }

  router.push({
    name:
      normalizedSection === 'resumo'
        ? 'lais_meu_aluguel_detail'
        : 'lais_meu_aluguel_detail_section',
    params: {
      accountId: currentAccountId.value,
      offerId: normalizedOfferId,
      ...(normalizedSection === 'resumo' ? {} : { section: normalizedSection }),
    },
  });
};

const selectFlow = flow => {
  selectedFlowId.value = flow.id;
  pushCurrentRentRoute({ offerId: flow.id, section: activeTab.value });
};

const selectLease = lease => {
  selectedLeaseId.value = lease.id;
  activeTab.value = 'operacao';
};

const selectTab = tabId => {
  activeTab.value = normalizeTab(tabId);
  pushCurrentRentRoute({ section: activeTab.value });
};

const numericValue = value => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const money = value =>
  value == null || value === ''
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(Number(value || 0));

const dateLabel = value => {
  if (!value) return 'Data não informada';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
};

const parseDate = value => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [year, month, day] = String(value).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const daysUntil = value => {
  const date = parseDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
};

const deadlineLabel = value => {
  const days = daysUntil(value);
  if (days === null) return 'Assinar contrato agora';
  if (days <= 0) return 'Assinar até hoje';
  if (days === 1) return 'Assinar até amanhã';
  return `Assinar até ${dateLabel(value)}`;
};

const firstName = value => {
  const label = String(value || '').trim();
  return label.split(/\s+/)[0] || 'Interessado';
};

const offerRentalBreakdown = offer =>
  offer?.metadata?.rental_breakdown || offer?.metadata?.rentalBreakdown || {};

const offerClosingFlow = offer =>
  offer?.metadata?.rental_closing ||
  offer?.metadata?.rentalClosing ||
  offer?.metadata?.contract_flow ||
  offer?.metadata?.contractFlow ||
  offer?.metadata?.closing ||
  {};

const valueFromSources = (sources, ...keys) => {
  const source = sources.find(currentSource =>
    keys.some(currentKey => {
      const value = currentSource?.[currentKey];
      return value !== undefined && value !== null && value !== '';
    })
  );
  const key = keys.find(currentKey => {
    const value = source?.[currentKey];
    return value !== undefined && value !== null && value !== '';
  });
  return key ? source[key] : null;
};

const closingValue = (offer, ...keys) =>
  valueFromSources([offerClosingFlow(offer), offer?.metadata || {}], ...keys);

const normalizedStatus = value => String(value || '').toLowerCase();

const statusIn = (value, statuses) =>
  statuses.includes(normalizedStatus(value));

const openClosingLink = (url, fallbackMessage) => {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  useAlert(fallbackMessage);
};

const tenantName = computed(
  () =>
    activeContract.value?.contact_name ||
    activeContract.value?.metadata?.requester_name ||
    activeContract.value?.contact_email ||
    activeContract.value?.contact_phone_number ||
    'Interessado'
);

const tenantDescription = computed(() => {
  const metadata = activeContract.value?.metadata || {};
  const rentalBreakdown = offerRentalBreakdown(activeContract.value);
  return (
    metadata.requester_description ||
    metadata.presentation ||
    rentalBreakdown.presentation ||
    activeContract.value?.notes ||
    'Apresentação do interessado ainda não informada.'
  );
});

const offerReference = computed(
  () =>
    activeContract.value?.booking_reference ||
    activeContract.value?.token?.slice(0, 8) ||
    activeContract.value?.id ||
    '-'
);

const rentalMetadataNumber = (offer, key) => {
  const value = offerRentalBreakdown(offer)?.[key];
  return numericValue(value);
};

const rentalMetadataBoolean = (offer, key) => {
  const value = offerRentalBreakdown(offer)?.[key];
  return value === true || value === 'true';
};

const residentsCountLabel = residentsCount => {
  const count = String(residentsCount);
  if (count === '+6' || Number(count) >= 6) return '6 ou mais moradores';
  if (count === '1') return '1 morador';
  return `${count} moradores`;
};

const residentsSituationFromValues = (residentsCount, hasPets) => {
  if (!residentsCount) return '';
  return `${residentsCountLabel(residentsCount)}${
    hasPets ? ' com animal de estimação' : ''
  }`;
};

const residentsSituation = computed(
  () =>
    offerRentalBreakdown(activeContract.value)?.residents_situation ||
    residentsSituationFromValues(
      rentalMetadataNumber(activeContract.value, 'residents_count'),
      rentalMetadataBoolean(activeContract.value, 'has_pets')
    ) ||
    'Moradores não informados'
);

const offerAmount = offer => numericValue(offer?.offer_amount);

const rentalIptuValue = offer =>
  rentalMetadataNumber(offer, 'iptu_amount') ??
  numericValue(offer?.imovel_valor_iptu);

const rentalCondominiumValue = offer =>
  rentalMetadataNumber(offer, 'condominium_amount') ??
  numericValue(offer?.imovel_valor_condominio);

const rentalIptuMonthlyValue = offer => {
  const monthly =
    rentalMetadataNumber(offer, 'iptu_monthly_amount') ??
    rentalMetadataNumber(offer, 'monthly_iptu_amount') ??
    rentalMetadataNumber(offer, 'property_tax_monthly_amount') ??
    rentalMetadataNumber(offer, 'monthly_property_tax_amount');
  if (monthly != null) return monthly;

  const annual = rentalIptuValue(offer);
  return annual == null ? null : annual / 12;
};

const rentalBreakdownNumber = (offer, ...keys) =>
  numericValue(
    valueFromSources(
      [offerRentalBreakdown(offer), offer?.metadata || {}, offer || {}],
      ...keys
    )
  );

const homeProtectionValue = offer =>
  rentalBreakdownNumber(
    offer,
    'home_protection',
    'homeProtection',
    'home_protection_amount',
    'homeProtectionAmount',
    'fire_insurance',
    'fireInsurance',
    'fire_insurance_amount',
    'fireInsuranceAmount',
    'insurance_amount',
    'insuranceAmount'
  );

const tenantServiceFeeValue = offer =>
  rentalBreakdownNumber(
    offer,
    'tenant_service_fee',
    'tenantServiceFee',
    'tenant_service_fee_amount',
    'tenantServiceFeeAmount',
    'service_fee',
    'serviceFee',
    'service_fee_amount',
    'serviceFeeAmount'
  );

const normalizeRentalGuaranteeType = value => {
  const normalized = String(value || '').toLowerCase();
  if (
    [
      RENTAL_GUARANTEE_TYPES.STANDALONE,
      'standalone_guarantee',
      'fianca_simples',
      'fiança simples',
      'owner',
    ].includes(normalized)
  ) {
    return RENTAL_GUARANTEE_TYPES.STANDALONE;
  }

  if (
    [
      RENTAL_GUARANTEE_TYPES.THIRD_PARTY_GUARANTEE,
      'third_party',
      'third_party_guarantee',
      'fianca_locaticia',
      'fiança locatícia',
      'imobiliaria',
      'terceiro',
    ].includes(normalized)
  ) {
    return RENTAL_GUARANTEE_TYPES.THIRD_PARTY_GUARANTEE;
  }

  return null;
};

const rentalGuaranteeType = offer =>
  normalizeRentalGuaranteeType(
    valueFromSources(
      [offerRentalBreakdown(offer), offer?.metadata || {}, offer || {}],
      'rental_guarantee_type',
      'rentalGuaranteeType',
      'rental_guarantee',
      'rentalGuarantee',
      'garantia_aluguel',
      'garantiaAluguel',
      'rental_administrator',
      'rentalAdministrator'
    )
  );

const rentalCountryCode = offer =>
  valueFromSources(
    [offerRentalBreakdown(offer), offer?.metadata || {}, offer || {}],
    'country_code',
    'countryCode'
  ) || 'BR';

const rentalAdminFeeForOffer = offer => {
  const metadataValue = rentalMetadataNumber(offer, 'admin_fee_value');
  if (metadataValue != null) return metadataValue;

  const amount = offerAmount(offer);
  if (amount == null) return null;

  const percentage = rentalMetadataNumber(offer, 'admin_fee_percentage') ?? 10;
  const minimum = rentalMetadataNumber(offer, 'minimum_fee_value') ?? 0;
  return Math.max((amount * percentage) / 100, minimum);
};

const ownerReceiveValue = offer => {
  const amount = offerAmount(offer);
  if (amount == null) return null;

  const metadataValue = rentalMetadataNumber(offer, 'owner_receive_amount');
  if (metadataValue != null) return metadataValue;

  return Math.max(amount - (rentalAdminFeeForOffer(offer) || 0), 0);
};

const rentalAdminFeePercentageLabel = offer => {
  const percentage = rentalMetadataNumber(offer, 'admin_fee_percentage') ?? 10;
  return `Taxa de adm. (${percentage}%)`;
};

const priceBreakdownSourceRows = offer =>
  [
    {
      kind: PRICE_ROW_KINDS.RENT_PRICE,
      label: 'Aluguel',
      value: offerAmount(offer) ?? numericValue(offer?.imovel_valor_aluguel),
    },
    {
      kind: PRICE_ROW_KINDS.CONDO_PRICE,
      label: 'Condomínio',
      value: rentalCondominiumValue(offer),
    },
    {
      kind: PRICE_ROW_KINDS.PROPERTY_TAX,
      label: 'IPTU mensal',
      value: rentalIptuMonthlyValue(offer),
      annualValue: rentalIptuValue(offer),
    },
    {
      kind: PRICE_ROW_KINDS.HOME_PROTECTION,
      label: 'Seguro incêndio',
      value: homeProtectionValue(offer),
    },
    {
      kind: PRICE_ROW_KINDS.TENANT_SERVICE_FEE,
      label: 'Taxa de serviço',
      value: tenantServiceFeeValue(offer),
    },
  ].filter(row => row.value != null);

const priceBreakdownRows = computed(() => {
  const offer = activeContract.value;
  if (!offer) return [];

  return buildPriceBreakdownRows(
    {
      finalidade: 'aluguel',
      rentalGuarantee: rentalGuaranteeType(offer),
    },
    {
      rows: priceBreakdownSourceRows(offer),
      countryCode: rentalCountryCode(offer),
      companyName: 'Imudei',
    }
  );
});

const ownerValueRows = computed(() => {
  const offer = activeContract.value;
  if (!offer) return [];

  return [
    {
      label: rentalAdminFeePercentageLabel(offer),
      value: rentalAdminFeeForOffer(offer),
    },
    {
      label: 'Taxa mínima de adm.',
      value: rentalMetadataNumber(offer, 'minimum_fee_value'),
    },
    {
      label: 'Proprietário vai receber',
      value: ownerReceiveValue(offer),
      highlight: true,
    },
  ].filter(row => row.value != null);
});

const customSolicitationLabel = type =>
  ({
    Add: 'Adicionar',
    Adicionar: 'Adicionar',
    Remove: 'Remover',
    Remover: 'Remover',
    RepairMaintenance: 'Reparar',
    Reparar: 'Reparar',
    ModifyReplace: 'Modificar',
    Modificar: 'Modificar',
  })[type] || 'Modificar';

const customSolicitations = computed(() => {
  const offer = activeContract.value;
  const raw =
    offerRentalBreakdown(offer)?.custom_solicitations ||
    offerRentalBreakdown(offer)?.customSolicitations ||
    offer?.metadata?.custom_solicitations ||
    offer?.metadata?.customSolicitations ||
    offer?.metadata?.adjustments ||
    [];

  if (!Array.isArray(raw) || !raw.length) return [];

  return raw.map(item => {
    if (typeof item === 'string') {
      return { type: 'Modificar', description: item };
    }

    return {
      type: customSolicitationLabel(item.type || item.kind || item.action),
      description:
        item.description || item.message || item.label || item.title || '',
    };
  });
});

const customSolicitationsCountLabel = computed(() => {
  const count = customSolicitations.value.length;
  return `${count} ${count === 1 ? 'item' : 'itens'}`;
});

const hasPaidGuarantee = computed(() => {
  const metadata = activeContract.value?.metadata || {};
  return (
    metadata.has_paid_the_guarantee === true ||
    metadata.hasPaidTheGuarantee === true ||
    Boolean(metadata.guarantee_payment_date || metadata.guaranteePaymentDate)
  );
});

const guaranteePaymentDate = computed(
  () =>
    activeContract.value?.metadata?.guarantee_payment_date ||
    activeContract.value?.metadata?.guaranteePaymentDate
);

const offerAcceptedLabel = computed(() =>
  activeContract.value?.metadata?.automatically_accepted ||
  activeContract.value?.metadata?.automaticallyAccepted
    ? `Proposta aceita automaticamente em ${dateLabel(activeContract.value?.decided_at)}`
    : `Proposta aceita em ${dateLabel(activeContract.value?.decided_at)}`
);

const rentOfferTitle = computed(() => {
  const originalValue = numericValue(
    activeContract.value?.imovel_valor_aluguel
  );
  const proposalValue = offerAmount(activeContract.value);
  if (originalValue && proposalValue && originalValue === proposalValue) {
    return 'Proposta no valor do anúncio';
  }
  return 'Proposta de aluguel';
});

const ownerName = computed(
  () =>
    closingValue(
      activeContract.value,
      'owner_name',
      'ownerName',
      'supply_name',
      'supplyName'
    ) || 'proprietário'
);

const demandName = computed(() => firstName(tenantName.value));
const supplyName = computed(() => firstName(ownerName.value));

const contractStartDate = computed(() =>
  closingValue(
    activeContract.value,
    'contract_start_date',
    'contractStartDate',
    'start_date',
    'startDate'
  )
);

const signatureDeadline = computed(
  () =>
    closingValue(
      activeContract.value,
      'signature_deadline_date',
      'signatureDeadlineDate',
      'contract_deadline_date',
      'contractDeadlineDate',
      'signature_deadline_at',
      'signatureDeadlineAt',
      'contract_deadline_at',
      'contractDeadlineAt'
    ) || activeContract.value?.metadata?.contract_deadline_date
);

const contractEmails = computed(() => {
  const raw = closingValue(
    activeContract.value,
    'contract_emails',
    'contractEmails',
    'signature_emails',
    'signatureEmails',
    'emails'
  );
  if (Array.isArray(raw)) return raw.filter(Boolean).join(', ');
  return raw || activeContract.value?.contact_email || '';
});

const ownerDocumentsCard = computed(() => {
  const offer = activeContract.value;
  const status = closingValue(
    offer,
    'owner_documents_status',
    'ownerDocumentsStatus',
    'supply_documents_status',
    'supplyDocumentsStatus'
  );
  const hasRejectedDocs =
    statusIn(status, [
      'rejected',
      'reprovado',
      'needs_resubmission',
      'problem',
    ]) ||
    Boolean(
      closingValue(
        offer,
        'owner_documents_rejected_at',
        'ownerDocumentsRejectedAt'
      )
    );
  const hasApprovedDocs =
    statusIn(status, ['approved', 'aprovado']) ||
    Boolean(
      closingValue(
        offer,
        'owner_documents_approved_at',
        'ownerDocumentsApprovedAt'
      )
    );
  const hasSentDocs =
    hasApprovedDocs ||
    statusIn(status, ['sent', 'submitted', 'enviado', 'under_review']) ||
    Boolean(
      closingValue(offer, 'owner_documents_sent_at', 'ownerDocumentsSentAt')
    );

  if (hasRejectedDocs) {
    return {
      title: 'Documentação do proprietário',
      status: 'Reenvie os documentos para análise',
      description:
        'Houve um problema com a documentação. Por favor, envie novamente de acordo com as orientações do nosso time, ou entre em contato conosco.',
      state: 'current',
      actionLabel: 'Reenviar documentos',
      actionIcon: 'i-lucide-upload-cloud',
      actionUrl: closingValue(
        offer,
        'owner_documents_url',
        'ownerDocumentsUrl',
        'documents_url',
        'documentsUrl'
      ),
      fallbackMessage:
        'Ainda não há link de reenvio de documentos para esta proposta.',
    };
  }

  if (hasApprovedDocs || hasSentDocs) {
    return {
      title: 'Documentação do proprietário',
      status: hasApprovedDocs
        ? 'Proprietário enviou a documentação'
        : 'Analisando documentos enviados',
      description: 'Com tudo certo, em poucos dias o contrato será preparado.',
      state: hasApprovedDocs ? 'done' : 'current',
    };
  }

  return {
    title: 'Documentação do proprietário',
    status: 'Enviar os documentos para análise',
    description:
      'Somente usuários com perfil administrador podem enviar a documentação',
    state: 'current',
    actionLabel: 'Enviar documentos',
    actionIcon: 'i-lucide-upload-cloud',
    actionUrl: closingValue(
      offer,
      'owner_documents_url',
      'ownerDocumentsUrl',
      'documents_url',
      'documentsUrl'
    ),
    fallbackMessage:
      'Ainda não há link de envio de documentos para esta proposta.',
  };
});

const tenantDocumentsCard = computed(() => {
  const offer = activeContract.value;
  const status = closingValue(
    offer,
    'tenant_documents_status',
    'tenantDocumentsStatus',
    'demand_documents_status',
    'demandDocumentsStatus'
  );
  const hasApprovedDocs =
    statusIn(status, ['approved', 'aprovado']) ||
    Boolean(
      closingValue(
        offer,
        'tenant_documents_approved_at',
        'tenantDocumentsApprovedAt'
      )
    );
  const hasSentDocs =
    hasApprovedDocs ||
    statusIn(status, ['sent', 'submitted', 'enviado', 'under_review']) ||
    Boolean(
      closingValue(offer, 'tenant_documents_sent_at', 'tenantDocumentsSentAt')
    );

  if (hasApprovedDocs) {
    return {
      title: 'Documentação do interessado',
      status: 'Documentação do interessado aprovada',
      description: `${demandName.value} já enviou os documentos necessários para o contrato.`,
      state: 'done',
    };
  }

  if (hasSentDocs) {
    return {
      title: 'Documentação do interessado',
      status: 'Analisando documentação',
      description: 'Aguardando a análise dos documentos do interessado.',
      state: 'current',
    };
  }

  return {
    title: 'Documentação do interessado',
    status: 'Aguardando envio dos documentos',
    description: `Aguardando interessado ${demandName.value}.`,
    state: 'pending',
  };
});

const contractStatus = computed(() =>
  normalizedStatus(
    closingValue(
      activeContract.value,
      'contract_status',
      'contractStatus',
      'status'
    )
  )
);

const isContractSigned = computed(
  () =>
    statusIn(contractStatus.value, ['signed', 'assinado', 'completed']) ||
    Boolean(
      closingValue(
        activeContract.value,
        'contract_signed_at',
        'contractSignedAt'
      )
    )
);

const isContractPrepared = computed(
  () =>
    isContractSigned.value ||
    statusIn(contractStatus.value, [
      'prepared',
      'sent',
      'signature',
      'waiting_signature',
    ]) ||
    Boolean(
      closingValue(
        activeContract.value,
        'contract_prepared_at',
        'contractPreparedAt',
        'contract_sent_at',
        'contractSentAt'
      )
    )
);

const isContractSent = computed(
  () =>
    isContractPrepared.value ||
    Boolean(
      closingValue(activeContract.value, 'contract_sent_at', 'contractSentAt')
    )
);

const contractPreparationCard = computed(() => {
  if (isContractPrepared.value) {
    return {
      title: 'Preparação do contrato',
      status: 'Contrato preparado',
      description:
        'O contrato está pronto para assinar e foi enviado por e-mail!',
      state: 'done',
      actionLabel: 'Reenviar contrato',
      actionIcon: 'i-lucide-send',
      actionUrl: closingValue(
        activeContract.value,
        'resend_contract_url',
        'resendContractUrl'
      ),
      fallbackMessage:
        'Ainda não há link de reenvio do contrato para esta proposta.',
    };
  }

  return {
    title: 'Preparação do contrato',
    status:
      'Estamos preparando o contrato e enviaremos para o e-mail dos envolvidos.',
    description: 'Com tudo certo, em poucos dias o contrato será preparado.',
    state: 'pending',
  };
});

const tenantSignedAt = computed(() =>
  closingValue(
    activeContract.value,
    'tenant_signed_at',
    'tenantSignedAt',
    'demand_signed_at',
    'demandSignedAt'
  )
);

const ownerSignedAt = computed(() =>
  closingValue(
    activeContract.value,
    'owner_signed_at',
    'ownerSignedAt',
    'supply_signed_at',
    'supplySignedAt'
  )
);

const signatureCard = computed(() => {
  if (isContractSigned.value) {
    return {
      title: 'Assinatura do contrato',
      status: 'Contrato assinado!',
      description:
        'Todos já assinaram o contrato, agora é só acompanhar os próximos passos pela carteira de imóveis.',
      state: 'done',
    };
  }

  if (isContractSent.value) {
    return {
      title: 'Assinatura do contrato',
      status: 'O contrato está pronto para assinar e foi enviado por e-mail!',
      description: `${deadlineLabel(signatureDeadline.value)}. ${
        contractStartDate.value
          ? `A data de início do contrato será em ${dateLabel(
              contractStartDate.value
            )}.`
          : 'Aguardando assinatura dos envolvidos.'
      }`,
      state: 'current',
      actionLabel: 'Assinar contrato',
      actionIcon: 'i-lucide-pen-line',
      actionUrl: closingValue(
        activeContract.value,
        'signature_url',
        'signatureUrl',
        'contract_url',
        'contractUrl'
      ),
      fallbackMessage: 'Ainda não há link de assinatura para esta proposta.',
    };
  }

  return {
    title: 'Assinatura do contrato',
    status: 'O contrato será enviado por e-mail em alguns instantes...',
    description: 'Aguardando preparação do contrato.',
    state: 'pending',
  };
});

const contractProgressCards = computed(() => [
  ownerDocumentsCard.value,
  tenantDocumentsCard.value,
  contractPreparationCard.value,
  signatureCard.value,
]);

const contractHero = computed(() => {
  if (isContractSigned.value) {
    return {
      title: 'Contrato assinado!',
      description:
        'Todos já assinaram o contrato, agora é só acompanhar os próximos passos pela carteira de imóveis.',
      icon: 'i-lucide-badge-check',
    };
  }

  return {
    title: 'Proposta aceita',
    description:
      'Acompanhe a documentação do proprietário, a documentação do interessado, a preparação e a assinatura do contrato.',
    icon: 'i-lucide-file-check-2',
  };
});

const signatureRows = computed(() => [
  {
    label: tenantSignedAt.value
      ? `Inquilino ${demandName.value} assinou o contrato em ${dateLabel(
          tenantSignedAt.value
        )}`
      : `Aguardando assinatura do inquilino ${demandName.value}`,
    done: Boolean(tenantSignedAt.value || isContractSigned.value),
  },
  {
    label: ownerSignedAt.value
      ? `Proprietário ${supplyName.value} assinou o contrato em ${dateLabel(
          ownerSignedAt.value
        )}`
      : `Aguardando assinatura do proprietário ${supplyName.value}`,
    done: Boolean(ownerSignedAt.value || isContractSigned.value),
  },
]);

const timelineEvents = computed(() => {
  if (!activeContract.value) return [];

  return [
    {
      label: 'Reservou o imóvel em',
      date: activeContract.value.submitted_at,
      description: `Referência ${offerReference.value}`,
      done: true,
    },
    {
      label: offerAcceptedLabel.value,
      date: activeContract.value.decided_at,
      description: 'Proposta para fechar contrato',
      done: true,
    },
    hasPaidGuarantee.value && {
      label: 'Garantia paga em',
      date: guaranteePaymentDate.value,
      description: `${firstName(tenantName.value)} pagou a garantia`,
      done: true,
    },
    ...contractProgressCards.value.map(card => ({
      label: card.title,
      description: card.status,
      done: card.state === 'done',
    })),
  ].filter(Boolean);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="min-w-0">
    <LaisHeader
      title="Meu aluguel"
      subtitle="Contrato, valores, apresentação e linha do tempo do aluguel em um só lugar."
    >
      <template #actions>
        <router-link
          :to="{
            name: 'lais_tenants_rent_flows',
            query: { status: 'accepted' },
          }"
          class="inline-flex min-h-[40px] items-center rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-text hover:border-lais-primary hover:text-lais-link"
        >
          Propostas aceitas no imóvel
        </router-link>
      </template>
    </LaisHeader>

    <div
      v-if="rentFlows.length > 1"
      class="mb-4 flex gap-2 overflow-x-auto no-scrollbar"
    >
      <button
        v-for="flow in rentFlows"
        :key="flow.id"
        type="button"
        class="rounded-full border px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap"
        :class="
          String(selectedFlowId || activeContract?.id) === String(flow.id)
            ? 'border-lais-primary bg-lais-primary text-white'
            : 'border-lais-border bg-white text-lais-text'
        "
        @click="selectFlow(flow)"
      >
        {{ flow.imovel_titulo || `Imóvel #${flow.imovel_id}` }}
      </button>
    </div>

    <div
      v-if="activeLeases.length"
      class="mb-4 flex gap-2 overflow-x-auto no-scrollbar"
    >
      <button
        v-for="lease in activeLeases"
        :key="`lease-${lease.id}`"
        type="button"
        class="rounded-full border px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap"
        :class="
          String(selectedLeaseId) === String(lease.id)
            ? 'border-lais-primary bg-lais-primary text-white'
            : 'border-lais-border bg-white text-lais-text'
        "
        @click="selectLease(lease)"
      >
        {{ lease.imovel_title || lease.reference || `Locação #${lease.id}` }}
      </button>
    </div>

    <div class="mb-4 flex gap-2 overflow-x-auto no-scrollbar">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="rounded-full border px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap"
        :class="
          activeTab === tab.id
            ? 'border-lais-primary bg-lais-primary text-white'
            : 'border-lais-border bg-white text-lais-text'
        "
        @click="selectTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="loading" class="lais-card p-6 text-[13px] text-lais-text-muted">
      Carregando dados do aluguel...
    </div>

    <LaisCard
      v-else-if="loadError"
      title="Não foi possível carregar seu aluguel"
    >
      <p class="text-[13px] leading-6 text-lais-text-muted">
        {{ loadError }}
      </p>
      <template #footer>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          @click="fetchRentFlows"
        >
          Tentar novamente
        </LaisPrimaryButton>
      </template>
    </LaisCard>

    <div v-else-if="activeTab === 'operacao'" class="grid gap-4">
      <div
        v-if="leasesLoading"
        class="lais-card p-6 text-[13px] text-lais-text-muted"
      >
        Carregando locações ativas...
      </div>
      <LaisCard
        v-else-if="leasesError"
        title="Não foi possível carregar a locação"
      >
        <p class="text-[13px] leading-6 text-lais-text-muted">
          {{ leasesError }}
        </p>
        <template #footer>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-refresh-cw"
            @click="fetchActiveLeases"
          >
            Tentar novamente
          </LaisPrimaryButton>
        </template>
      </LaisCard>
      <LeaseOperationPanel
        v-else-if="selectedLeaseId"
        :lease-id="selectedLeaseId"
        title="Operação ativa do aluguel"
      />
      <LaisCard v-else title="Locação ativa ainda não materializada">
        <p class="text-[13px] leading-6 text-lais-text-muted">
          Ainda não existe contrato de locação ativo vinculado nesta conta.
          Quando a locação for materializada, cobranças, boletos persistidos,
          reajustes, repasses, vistorias, pendências e histórico aparecerão
          aqui.
        </p>
      </LaisCard>
    </div>

    <LaisCard v-else-if="!activeContract" title="Nenhum aluguel conectado">
      <p class="text-[13px] leading-6 text-lais-text-muted">
        Ainda não existe proposta de aluguel aceita vinculada a esta conta.
        Quando você tiver propostas aceitas, elas aparecerão aqui para que você
        possa avaliar e acompanhar.
      </p>
      <template #footer>
        <router-link
          :to="{ name: 'lais_tenants_rent_flows' }"
          class="inline-flex min-h-[40px] items-center rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-text hover:border-lais-primary hover:text-lais-link"
        >
          Ver propostas e reservas
        </router-link>
      </template>
    </LaisCard>

    <div
      v-else-if="activeTab === 'resumo'"
      class="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]"
    >
      <LaisCard title="Proposta para fechar contrato">
        <div class="grid gap-4">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="min-w-0">
              <p class="text-[13px] font-semibold text-lais-text">
                {{ tenantName }}
              </p>
              <p class="mt-1 text-[13px] text-lais-text-muted">
                {{ residentsSituation }}
              </p>
            </div>
            <span
              class="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700"
            >
              Proposta aceita
            </span>
          </div>

          <div class="rounded-lais border border-lais-border-soft px-4 py-3">
            <p class="text-[13px] font-semibold text-lais-text">
              {{ rentOfferTitle }}
            </p>
            <div class="mt-1 flex flex-wrap items-baseline gap-2">
              <strong
                class="text-[24px] leading-none text-lais-text font-redHatDisplay"
              >
                {{ money(activeContract.offer_amount) }}
              </strong>
              <span
                v-if="
                  activeContract.imovel_valor_aluguel &&
                  Number(activeContract.imovel_valor_aluguel) !==
                    Number(activeContract.offer_amount)
                "
                class="text-[13px] text-lais-text-muted line-through"
              >
                {{ money(activeContract.imovel_valor_aluguel) }}
              </span>
            </div>
          </div>

          <div class="grid gap-2 text-[13px] leading-6 text-lais-text-muted">
            <span>
              Imóvel:
              <strong class="text-lais-text">
                {{
                  activeContract.imovel_titulo ||
                  `Imóvel #${activeContract.imovel_id}`
                }}
              </strong>
            </span>
            <span v-if="activeContract.imovel_endereco">
              Endereço:
              <strong class="text-lais-text">{{
                activeContract.imovel_endereco
              }}</strong>
            </span>
            <span>
              Referência:
              <strong class="text-lais-text">{{ offerReference }}</strong>
            </span>
            <span>
              {{ offerAcceptedLabel }}
            </span>
            <span v-if="hasPaidGuarantee">
              Garantia paga em {{ dateLabel(guaranteePaymentDate) }}
            </span>
          </div>
        </div>
      </LaisCard>

      <LaisCard title="Apresentação">
        <div class="grid gap-3 text-[13px] leading-6 text-lais-text-muted">
          <p>
            <strong class="text-lais-text">{{ tenantName }}</strong>
          </p>
          <p>{{ residentsSituation }}</p>
          <p>{{ tenantDescription }}</p>
        </div>
      </LaisCard>
    </div>

    <div
      v-else-if="activeTab === 'valores'"
      class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]"
    >
      <LaisPriceBreakdown :rows="priceBreakdownRows" start-expanded />

      <LaisCard title="Repasse ao proprietário">
        <div class="grid gap-3 text-[13px] leading-6 text-lais-text-muted">
          <p>
            A quebra ao lado mostra os valores mensais do aluguel. O repasse
            abaixo mantém as regras administrativas da proposta.
          </p>
          <div
            v-for="row in ownerValueRows"
            :key="row.label"
            class="flex items-center justify-between gap-3 rounded-lais border border-lais-border-soft px-3 py-2"
          >
            <span :class="row.highlight ? 'font-semibold text-lais-text' : ''">
              {{ row.label }}
            </span>
            <strong
              :class="row.highlight ? 'text-lais-primary' : 'text-lais-text'"
            >
              {{ money(row.value) }}
            </strong>
          </div>
        </div>
      </LaisCard>
    </div>

    <div v-else-if="activeTab === 'contrato'" class="grid gap-4">
      <LaisCard :title="contractHero.title">
        <div class="grid gap-4 text-[13px] leading-6 text-lais-text-body">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
            <span
              class="grid size-10 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
            >
              <span :class="`${contractHero.icon} size-5`" aria-hidden="true" />
            </span>
            <div class="min-w-0">
              <p>{{ contractHero.description }}</p>
              <p
                v-if="contractStartDate"
                class="mt-2 font-semibold text-lais-text"
              >
                A data de início do contrato será em
                {{ dateLabel(contractStartDate) }}.
              </p>
              <p v-if="contractEmails" class="mt-1 text-lais-text-muted">
                Contrato enviado para {{ contractEmails }}
              </p>
              <p v-if="signatureDeadline" class="mt-1 text-lais-text-muted">
                O prazo estimado é até {{ dateLabel(signatureDeadline) }}
              </p>
            </div>
          </div>
        </div>
        <template #footer>
          <router-link
            v-if="isContractSigned"
            :to="{ name: 'imoveis_index', query: { finalidade: 'aluguel' } }"
            class="inline-flex min-h-[40px] items-center rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-text hover:border-lais-primary hover:text-lais-link"
          >
            Acessar imóveis alugados
          </router-link>
        </template>
      </LaisCard>

      <div class="grid gap-4 lg:grid-cols-2">
        <section
          v-for="card in contractProgressCards"
          :key="card.title"
          class="rounded-lais border border-lais-border bg-white p-4"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border text-[12px]"
              :class="
                card.state === 'done'
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : card.state === 'current'
                    ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                    : 'border-lais-border bg-lais-bg-soft text-lais-text-muted'
              "
            >
              <span
                v-if="card.state === 'done'"
                class="i-lucide-check size-4"
                aria-hidden="true"
              />
            </span>
            <div class="min-w-0 flex-1">
              <p
                class="font-redHatDisplay text-[16px] font-semibold text-lais-text"
              >
                {{ card.title }}
              </p>
              <p class="mt-2 text-[13px] font-semibold text-lais-text">
                {{ card.status }}
              </p>
              <p class="mt-1 text-[13px] leading-6 text-lais-text-muted">
                {{ card.description }}
              </p>
              <LaisPrimaryButton
                v-if="card.actionLabel"
                class="mt-3"
                size="sm"
                variant="ghost"
                :icon="card.actionIcon"
                @click="openClosingLink(card.actionUrl, card.fallbackMessage)"
              >
                {{ card.actionLabel }}
              </LaisPrimaryButton>
            </div>
          </div>
        </section>
      </div>

      <LaisCard
        :title="
          isContractSigned ? 'Etapas concluídas' : 'Assinaturas do contrato'
        "
      >
        <div class="grid gap-3 text-[13px] text-lais-text-body">
          <div
            v-for="row in signatureRows"
            :key="row.label"
            class="flex items-start gap-3"
          >
            <span
              class="mt-1 size-2.5 shrink-0 rounded-full"
              :class="row.done ? 'bg-lais-primary' : 'bg-lais-border'"
            />
            <span>{{ row.label }}</span>
          </div>
        </div>
      </LaisCard>
    </div>

    <LaisCard v-else-if="activeTab === 'apresentacao'" title="Apresentação">
      <div class="grid gap-5 lg:grid-cols-2">
        <section class="grid gap-3">
          <h2
            class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
          >
            Detalhes da proposta
          </h2>
          <p class="text-[13px] leading-6 text-lais-text-muted">
            {{ offerAcceptedLabel }}
          </p>
          <p class="text-[13px] leading-6 text-lais-text-muted">
            {{ residentsSituation }}
          </p>
          <p class="text-[13px] leading-6 text-lais-text-muted">
            {{ tenantDescription }}
          </p>
        </section>

        <section class="grid gap-3">
          <div class="flex items-center justify-between gap-3">
            <h2
              class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
            >
              Ajustes no imóvel
            </h2>
            <span class="text-[13px] font-semibold text-lais-text-muted">
              {{ customSolicitationsCountLabel }}
            </span>
          </div>

          <div v-if="customSolicitations.length" class="grid gap-3">
            <article
              v-for="item in customSolicitations"
              :key="`${item.type}-${item.description}`"
              class="rounded-lais bg-lais-bg-muted px-4 py-3"
            >
              <p class="text-[12px] font-semibold text-lais-text-muted">
                {{ item.type }}
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ tenantName }}
              </p>
              <p class="mt-1 text-[13px] text-lais-text-muted">
                {{ item.description || 'Ajuste solicitado no imóvel.' }}
              </p>
            </article>
          </div>
          <p v-else class="text-[13px] leading-6 text-lais-text-muted">
            Sem ajustes no imóvel.
          </p>
        </section>
      </div>
    </LaisCard>

    <LaisCard v-else title="Linha do tempo do aluguel">
      <ol class="grid gap-3 text-[13px] leading-6 text-lais-text-muted">
        <li
          v-for="event in timelineEvents"
          :key="`${event.label}-${event.date || event.description}`"
          class="rounded-lais border border-lais-border bg-white px-4 py-3"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-1 size-2.5 shrink-0 rounded-full"
              :class="event.done ? 'bg-lais-primary' : 'bg-lais-border'"
            />
            <div class="min-w-0">
              <strong class="block text-lais-text">
                {{ event.label }}
                <template
                  v-if="
                    event.date &&
                    !String(event.label).includes(dateLabel(event.date))
                  "
                >
                  {{ dateLabel(event.date) }}
                </template>
              </strong>
              <span>{{ event.description }}</span>
            </div>
          </div>
        </li>
      </ol>
      <template #footer>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          @click="fetchRentFlows"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisCard>
  </section>
</template>
